import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { adsApiCall, adsApi } from "./api";
import { Loader2, Sparkles, Zap, ExternalLink, Heart, Gift, Mail, CheckCircle2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import CanvasThumb from "./CanvasThumb";
import "./ads-creator.css";

export default function PublicCampaign() {
  const { campaignId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ogUrl = `${adsApi.defaults.baseURL}/campaigns/${campaignId}/og.png`;
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/c/${campaignId}` : "";

  useEffect(() => {
    (async () => {
      try {
        const res = await adsApiCall.publicCampaign(campaignId);
        setData(res);
        document.title = `${res.title} — 25-ad campaign | TopRankPulse`;
        // Fire-and-forget analytics
        adsApiCall.trackCampaign(campaignId, "view").catch(() => {});
        // Referral attribution — new visitors get tagged for 30 days
        try {
          localStorage.setItem("trp_ref_campaign", campaignId);
          localStorage.setItem("trp_ref_campaign_ts", String(Date.now()));
        } catch {}
      } catch (e) {
        setError(e?.response?.status === 404 ? "Campaign not found or not public." : "Couldn't load campaign.");
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  function trackCta() {
    adsApiCall.trackCampaign(campaignId, "cta_click").catch(() => {});
  }

  if (loading) {
    return (
      <div className="ads-root" data-testid="public-campaign-loading">
        <div className="ads-empty-state"><Loader2 className="animate-spin" /> Loading campaign…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ads-root" data-testid="public-campaign-error">
        <div className="ads-empty-state">
          <div style={{ marginBottom: 12 }}>{error}</div>
          <Link to="/ads-creator" className="ads-btn ads-btn-primary">Create your own 25-ad campaign</Link>
        </div>
      </div>
    );
  }

  // Group by angle for a nicer narrative layout
  const byAngle = {};
  for (const p of data.projects) {
    const a = p.angle || "variant";
    if (!byAngle[a]) byAngle[a] = [];
    byAngle[a].push(p);
  }

  return (
    <div className="ads-root" data-testid="public-campaign">
      <Helmet>
        <title>{`${data.title} — 25-ad campaign | TopRankPulse`}</title>
        <meta name="description" content={`A full ${data.count}-ad campaign across ${data.angles.length} angles × 5 formats — generated in one minute with TopRankPulse.`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${data.title} — 25 ads in one prompt`} />
        <meta property="og:description" content={`${data.count} ads · ${data.angles.length} angles · 5 formats — made with TopRankPulse`} />
        <meta property="og:image" content={ogUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${data.title} — 25 ads in one prompt`} />
        <meta name="twitter:description" content="A full ad campaign generated in under a minute with TopRankPulse." />
        <meta name="twitter:image" content={ogUrl} />
      </Helmet>
      <Toaster position="top-right" theme="dark" />
      <div className="ads-hero" style={{ padding: "72px 28px 40px" }}>
        <div className="ads-container">
          <span className="ads-eyebrow"><Zap size={12} style={{ display: "inline", marginRight: 4 }} /> Instant Campaign · 25 ads · one prompt</span>
          <h1 className="ads-h1" style={{ fontSize: "clamp(36px, 5.4vw, 68px)" }}>{data.title}</h1>
          <p className="ads-lead" style={{ maxWidth: 720 }}>
            A full campaign — <b>{data.angles.length} angles × 5 formats</b> — generated from one sentence in under a minute.
            Want one like this for your business?
          </p>
          <div className="pc-referral-badge" data-testid="pc-referral-badge">
            <Gift size={14} />
            <span>Sign up via this link and get <b>3 free ad credits</b> on us — and you support the creator.</span>
          </div>
          <div className="ads-cta-row">
            <Link to="/ads-creator/instant-campaign" className="ads-btn ads-btn-primary" data-testid="pc-cta-create" onClick={trackCta}>
              <Sparkles size={18} /> Create your own 25 ads
            </Link>
            <Link
              to={`/ads-creator/instant-campaign?remix=${data.slug || data.campaign_id}`}
              className="ads-btn ads-btn-ghost"
              data-testid="pc-cta-remix"
              onClick={trackCta}
            >
              <Sparkles size={16} /> Remix this for my business
            </Link>
            <Link to="/ads-creator" className="ads-btn ads-btn-ghost" data-testid="pc-cta-learn">
              <ExternalLink size={16} /> How it works
            </Link>
          </div>
        </div>
      </div>

      <div className="ads-section" style={{ paddingTop: 12 }}>
        <div className="ads-container">
          {Object.entries(byAngle).map(([angle, projects]) => (
            <div key={angle} className="pc-angle-block" data-testid={`pc-angle-${angle}`}>
              <div className="pc-angle-head">
                <span className="pc-angle-badge">{angle.toUpperCase()}</span>
                <span className="pc-angle-count">{projects.length} formats</span>
              </div>
              <div className="pc-grid">
                {projects.map((p) => (
                  <div key={p.project_id} className="pc-card" data-testid={`pc-card-${p.project_id}`}>
                    <div className="pc-thumb-wrap">
                      <CanvasThumb
                        canvasJson={p.canvas_json}
                        width={p.width}
                        height={p.height}
                        maxWidth={300}
                      />
                      <div className="pc-watermark">Made with <b>TopRankPulse</b></div>
                    </div>
                    <div className="pc-meta">
                      <span className="pc-fmt">{p.format}</span>
                      <span className="pc-dims">{p.width}×{p.height}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Final CTA */}
          <div className="pc-final-cta" data-testid="pc-final-cta">
            <h2 className="ads-section-title">Your 25-ad campaign — one prompt away</h2>
            <p className="ads-section-sub">No designer. No agency. No late nights in Canva.</p>
            {data.leads_enabled && <LeadWidget campaignId={campaignId} />}
            <Link to="/ads-creator/instant-campaign" className="ads-btn ads-btn-primary" style={{ padding: "14px 24px", fontSize: 16 }} onClick={trackCta}>
              <Sparkles size={18} /> Start now — 3 ads free
            </Link>
          </div>
        </div>
      </div>

      {/* Prominent brand footer banner */}
      <div className="pc-brand-footer" data-testid="pc-brand-footer">
        <div className="ads-container pc-brand-row">
          <div className="pc-brand-mark">
            <Heart size={14} fill="currentColor" />
            <span>Created with <b>TopRankPulse</b></span>
          </div>
          <Link to="/ads-creator" className="ads-btn ads-btn-ghost" style={{ padding: "6px 12px", fontSize: 13 }} data-testid="pc-brand-link">
            Visit TopRankPulse <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function LeadWidget({ campaignId }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [captured, setCaptured] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const visitor_id = (() => {
        try { return localStorage.getItem("trp_vid") || ""; } catch { return ""; }
      })();
      const language = (typeof navigator !== "undefined" && navigator.language || "en").slice(0, 2);
      await adsApiCall.captureLead(campaignId, { email: email.trim().toLowerCase(), language, visitor_id });
      setCaptured(true);
      toast.success("Check your inbox for your 3-credit link!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't capture email");
    } finally {
      setLoading(false);
    }
  }

  if (captured) {
    return (
      <div className="pc-lead-captured" data-testid="pc-lead-captured">
        <CheckCircle2 size={20} style={{ color: "#4ade80" }} />
        <div>
          <div className="pc-lead-captured-title">You're on the list!</div>
          <div className="pc-lead-captured-sub">We've sent your 3-credit claim link. Check your inbox.</div>
        </div>
      </div>
    );
  }

  return (
    <form className="pc-lead-form" onSubmit={submit} data-testid="pc-lead-form">
      <div className="pc-lead-intro">
        <Mail size={16} style={{ color: "var(--ads-accent)" }} />
        <span>Want to save this campaign for later? We'll email it to you + 3 free ad credits.</span>
      </div>
      <div className="pc-lead-row">
        <input
          type="email"
          className="pc-lead-input"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="pc-lead-input"
        />
        <button
          type="submit"
          className="ads-btn ads-btn-primary"
          disabled={loading}
          data-testid="pc-lead-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Send me the link
        </button>
      </div>
      <div className="pc-lead-hint">Double opt-in · No spam · Unsubscribe anytime.</div>
    </form>
  );
}
